/**
 * Configurable collection pagination core.
 *
 * Reads product counts from #product-grid data attributes:
 * - desktop/mobile initial count
 * - desktop/mobile per-load count
 *
 * The server fetch size is the largest configured value, while this script
 * trims and appends actual product cards per device. Banner cells stay in DOM
 * order and do not consume product count.
 */
(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';
  const DEFAULT_DESKTOP_COUNT = 48;
  const DEFAULT_MOBILE_COUNT = 20;
  const FACET_REINIT_DELAY = 150;

  class PaginationCore {
    static getGrid() {
      return document.querySelector('#product-grid');
    }

    static getMode() {
      const grid = PaginationCore.getGrid();
      return (grid && grid.dataset.paginationMode) || 'pagination';
    }

    static isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    static readCount(name, fallback) {
      const grid = PaginationCore.getGrid();
      const value = grid ? parseInt(grid.dataset[name] || '', 10) : NaN;
      if (Number.isFinite(value) && value > 0) return Math.min(value, 250);
      return fallback;
    }

    static getInitialCount() {
      return PaginationCore.isMobile()
        ? PaginationCore.readCount('mobileInitialCount', DEFAULT_MOBILE_COUNT)
        : PaginationCore.readCount('desktopInitialCount', DEFAULT_DESKTOP_COUNT);
    }

    static getLoadCount() {
      return PaginationCore.isMobile()
        ? PaginationCore.readCount('mobileLoadCount', DEFAULT_MOBILE_COUNT)
        : PaginationCore.readCount('desktopLoadCount', DEFAULT_DESKTOP_COUNT);
    }

    static isProduct(item) {
      if (!item || item.nodeType !== 1) return false;
      if (item.getAttribute('data-cell-type') === 'product') return true;
      return item.classList.contains('grid__item')
        && !item.classList.contains('banner-item')
        && !item.hasAttribute('data-banner')
        && item.getAttribute('data-cell-type') !== 'search-result';
    }

    static collectVisibleProducts(container) {
      return Array.from(container.children).filter(PaginationCore.isProduct);
    }

    static splitAfterProductCount(items, productLimit) {
      let productCount = 0;
      let cutIndex = items.length;

      for (let index = 0; index < items.length; index += 1) {
        if (PaginationCore.isProduct(items[index])) {
          productCount += 1;
        }

        if (productCount >= productLimit) {
          cutIndex = index + 1;
          break;
        }
      }

      return {
        visible: items.slice(0, cutIndex),
        overflow: items.slice(cutIndex),
        productCount
      };
    }

    static removeOverflow(container, productLimit) {
      const items = Array.from(container.children);
      const split = PaginationCore.splitAfterProductCount(items, productLimit);

      split.visible.forEach((item) => {
        if (item.parentNode === container) item.hidden = false;
      });

      split.overflow.forEach((item) => {
        if (item.parentNode === container) item.hidden = true;
      });

      return split.overflow;
    }

    static takeProductBatch(buffer, productLimit) {
      if (!buffer.length) return [];

      const split = PaginationCore.splitAfterProductCount(buffer, productLimit);
      buffer.splice(0, split.visible.length);
      return split.visible;
    }

    static async fetchProducts(url) {
      if (!url) return { items: [], nextUrl: null, doc: null };

      const finalUrl = new URL(url, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (key !== 'page') finalUrl.searchParams.set(key, value);
      });

      const response = await fetch(finalUrl.toString());
      if (!response.ok) throw new Error('PaginationCore: fetch failed ' + response.status);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('#product-grid .grid__item'));
      const nextLink = doc.querySelector('#AjaxinatePagination a[href]');

      return {
        items,
        nextUrl: nextLink ? nextLink.href : null,
        doc
      };
    }

    static appendItems(container, items) {
      if (!container || !items.length) return;
      const fragment = document.createDocumentFragment();
      items.forEach((item) => {
        item.hidden = false;
        if (item.parentNode !== container) fragment.appendChild(item);
      });
      if (fragment.childNodes.length > 0) container.appendChild(fragment);

      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger();
      }
    }

    static trimInitialForMode() {
      const grid = PaginationCore.getGrid();
      if (!grid || grid.dataset.initialCountApplied === 'true') return;
      grid.dataset.initialCountApplied = 'true';
    }
  }

  class LoadMoreManager {
    constructor() {
      this.container = PaginationCore.getGrid();
      this.button = document.querySelector('[data-load-more-btn]');
      this.buttonWrap = document.querySelector('.load-more-container');
      this.pagination = document.querySelector('#AjaxinatePagination');
      this.buffer = [];
      this.isLoading = false;
      this.hasAttemptedLoad = false;
      this.nextUrl = null;

      if (!this.container || !this.button) return;

      const link = this.pagination ? this.pagination.querySelector('a[href]') : null;
      this.nextUrl = link && link.dataset.localOverflow !== 'true' ? link.href : null;
      this.buffer.push(...PaginationCore.removeOverflow(this.container, PaginationCore.getInitialCount()));

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);
      this.refreshVisibility();
    }

    onClick(event) {
      event.preventDefault();
      this.load();
    }

    async load() {
      if (this.isLoading) return;
      this.hasAttemptedLoad = true;

      if (!this.hasEnoughBufferedProducts() && this.nextUrl) {
        await this.fetchUntilReady();
      }

      const batch = PaginationCore.takeProductBatch(this.buffer, PaginationCore.getLoadCount());
      if (batch.length) PaginationCore.appendItems(this.container, batch);

      if (!this.nextUrl && this.buffer.length === 0) this.disable();
      this.refreshVisibility();
    }

    hasEnoughBufferedProducts() {
      return this.buffer.filter(PaginationCore.isProduct).length >= PaginationCore.getLoadCount();
    }

    async fetchUntilReady() {
      this.isLoading = true;
      this.setLoadingState(true);

      try {
        while (!this.hasEnoughBufferedProducts() && this.nextUrl) {
          const result = await PaginationCore.fetchProducts(this.nextUrl);
          this.buffer.push(...result.items);
          this.nextUrl = result.nextUrl;
        }
      } catch (error) {
        console.error('LoadMoreManager:', error);
      } finally {
        this.setLoadingState(false);
        this.isLoading = false;
      }
    }

    setLoadingState(loading) {
      this.button.disabled = loading;
      this.button.classList.toggle('is-loading', loading);
      const label = this.button.querySelector('.load-more-button__label');
      const spinner = this.button.querySelector('.load-more-button__spinner');
      if (label) label.hidden = loading;
      if (spinner) spinner.hidden = !loading;
    }

    refreshVisibility() {
      if (!this.buttonWrap) return;
      if (!this.nextUrl && this.buffer.length === 0) {
        this.disable();
        return;
      }
      this.buttonWrap.hidden = false;
    }

    disable() {
      if (this.buttonWrap) this.buttonWrap.hidden = true;
      else if (this.button) this.button.hidden = true;
    }

    destroy() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }
  }

  function maybeInitLoadMore() {
    if (PaginationCore.getMode() !== 'load_more') return;
    if (window.loadMoreManagerInstance && typeof window.loadMoreManagerInstance.destroy === 'function') {
      window.loadMoreManagerInstance.destroy();
    }
    window.loadMoreManagerInstance = new LoadMoreManager();
  }

  function maybeTrimPagination() {
    if (PaginationCore.getMode() === 'pagination') {
      PaginationCore.trimInitialForMode();
    }
  }

  function watchFacetReplacements() {
    const container = document.getElementById('ProductGridContainer');
    if (!container || container.dataset.paginationObserverAttached === 'true') return;
    container.dataset.paginationObserverAttached = 'true';

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.id === 'product-grid' || (typeof node.querySelector === 'function' && node.querySelector('#product-grid'))) {
            setTimeout(start, FACET_REINIT_DELAY);
            return;
          }
        }
      }
    });

    observer.observe(container, { childList: true, subtree: false });
  }

  function start() {
    maybeInitLoadMore();
    maybeTrimPagination();
    watchFacetReplacements();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('facet:render', () => setTimeout(start, FACET_REINIT_DELAY));
  document.addEventListener('facet:filter:changed', () => setTimeout(start, FACET_REINIT_DELAY));

  window.PaginationCore = PaginationCore;
  window.LoadMoreManager = LoadMoreManager;
})();
